import React from "react";
import {
	Autocomplete,
	TextField,
	Chip,
	FormControl,
	Box,
	Typography,
	CircularProgress,
} from "@mui/material";
import { Event } from "../../domain/event.interface";

interface BehaviorSelectorProps {
	label?: string;
	placeholder?: string;
	helperText?: string;
	value: string[]; // Array of behavior IDs
	onChange: (behaviorIds: string[]) => void;
	events: Event[] | undefined;
	loading?: boolean;
	disabled?: boolean;
	error?: boolean;
	size?: "small" | "medium";
	required?: boolean;
}

export const BehaviorSelector: React.FC<BehaviorSelectorProps> = ({
	label = "Behaviors",
	placeholder = "Select behaviors/events",
	helperText = "Select events that represent behaviors",
	value = [],
	onChange,
	events = [],
	loading = false,
	disabled = false,
	error = false,
	size = "small",
	required = false,
}) => {
	const getSelectedEvents = (behaviorIds: string[]): Event[] => {
		if (!events || !behaviorIds) return [];
		return events.filter((event) => behaviorIds.includes(event._id));
	};

	const handleSelectionChange = (selectedEvents: Event[]) => {
		const behaviorIds = selectedEvents.map((event) => event._id);
		onChange(behaviorIds);
	};

	return (
		<FormControl fullWidth size={size}>
			<Autocomplete
				multiple
				options={events || []}
				getOptionLabel={(option: Event) => option.name}
				value={getSelectedEvents(value)}
				onChange={(_, selectedEvents) => handleSelectionChange(selectedEvents)}
				loading={loading}
				disabled={disabled || loading}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						placeholder={placeholder}
						helperText={helperText}
						required={required}
						error={error}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? (
										<CircularProgress color="inherit" size={20} />
									) : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
				renderTags={(selectedEvents, getTagProps) =>
					selectedEvents.map((option, index) => (
						<Chip
							variant="outlined"
							label={option.name}
							color="primary"
							size={size}
							{...getTagProps({ index })}
						/>
					))
				}
				renderOption={(props, option) => (
					<Box component="li" {...props}>
						<Box sx={{ flexGrow: 1 }}>
							<Typography variant="body2" component="div">
								{option.name}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								component="div"
							>
								{option.verificationType} • {option.validationType}
							</Typography>
						</Box>
					</Box>
				)}
				noOptionsText={loading ? "Loading events..." : "No events available"}
				filterOptions={(options, { inputValue }) => {
					return options.filter((option) =>
						option.name.toLowerCase().includes(inputValue.toLowerCase()),
					);
				}}
			/>
		</FormControl>
	);
};

export default BehaviorSelector;
